'use client';

import { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

export default function AboutSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef?.current) {
      observer?.observe(sectionRef?.current);
    }

    return () => {
      if (sectionRef?.current) {
        observer?.unobserve(sectionRef?.current);
      }
    };
  }, []);

  return (
    <section ref={sectionRef} className="py-20 md:py-32 px-6 bg-background">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-24 items-start">
          {/* Left Column */}
          <div className={`border-l-4 border-primary pl-6 ${isVisible ? 'animate-fadeInUp' : 'opacity-0'}`}>
            <span className="text-2xs font-semibold uppercase tracking-widest text-muted-foreground mb-4 block">
              About Us
            </span>
            <h2 className="font-display text-5xl md:text-6xl uppercase tracking-tighter leading-none text-foreground mb-6">
              Not Just Learning.<br />
              <span className="text-primary">Career Building.</span>
            </h2>
          </div>

          {/* Right Column */}
          <div className={`space-y-8 pt-4 ${isVisible ? 'animate-fadeInUp delay-200' : 'opacity-0'}`}>
            <p className="text-base leading-relaxed text-muted-foreground">
              Apollobits Academy, established in 2020, is a leading skill development institute focused on providing practical and technical training for students and professionals.
            </p>
            <p className="text-base leading-relaxed text-muted-foreground">
              We bridge the gap between education and industry by offering hands-on learning, real-world projects, and expert mentorship. Our mission is to empower learners with in-demand digital and technical skills that open doors to career growth and opportunities in today's competitive world.
            </p>

            {/* Stats */}
            <div className="flex gap-12 border-t border-border pt-8">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="CalendarIcon" size={24} className="text-primary" />
                  <span className="text-4xl md:text-5xl font-display text-foreground">5+</span>
                </div>
                <span className="text-2xs uppercase tracking-wider text-muted-foreground">
                  Years Experience
                </span>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Icon name="UserGroupIcon" size={24} className="text-primary" />
                  <span className="text-4xl md:text-5xl font-display text-foreground">1000+</span>
                </div>
                <span className="text-2xs uppercase tracking-wider text-muted-foreground">
                  Students Trained
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}